import { Stack } from '@mantine/core';
import { ListBox } from '@repo/foundations';
import { isNil, range } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import {
	FILTER_OPTIONS_DIVIDER,
	IS_NOT_SET_FILTER_ITEM,
	IS_SET_FILTER_ITEM,
} from '../constants';
import { FilterItemError } from '../FilterItemError';
import { FilterItemSkeleton } from '../FilterItemSkeleton';
import { FilterMenuItem } from '../FilterMenuItem';
import type {
	FilterDropdownConfigList,
	FilterItem,
	FilterOptionType,
	FilterValue,
	FilterValueType,
} from '../types';
import { FilterOperator, FilterType } from '../types';
import { useFilterDropdownList } from './useFilterDropdownList';

export interface FilterDropdownListProps {
	value: FilterValue;
	searchPlaceholder: FilterDropdownConfigList['searchPlaceholder'];
	getItems: FilterDropdownConfigList['getItems'];
	getItemsById?: FilterDropdownConfigList['getItemsById'];
	filterType?: FilterType;
	renderMenuItem?: (item: FilterItem) => React.ReactNode;
	onChange: (value: FilterValue, close: boolean) => void;
	hasIsNotSetOption?: boolean;
	hasIsSetOption?: boolean;
	excludeItems?: string[];
	handleOnSelect?: (item: FilterItem) => void;
	handleOnDeselect?: (item: FilterItem) => void;
	isNested?: boolean;
	filterOptionType?: FilterOptionType;
}

export function FilterDropdownList({
	getItems,
	getItemsById,
	value,
	searchPlaceholder = 'Search',
	filterType = FilterType.Multiple,
	renderMenuItem,
	onChange,
	hasIsNotSetOption,
	hasIsSetOption,
	excludeItems,
	handleOnDeselect,
	handleOnSelect,
	isNested = false,
	filterOptionType,
}: FilterDropdownListProps) {
	const { selected, unselected, loading, error, searchTerm, setSearchTerm } =
		useFilterDropdownList({
			getItems,
			getItemsById,
			value,
			hasIsNotSetOption,
			hasIsSetOption,
			excludeItems,
			isNested,
			filterOptionType,
		});

	const onSelect = useCallback(
		(item: FilterItem) => (close: boolean) => {
			if (item.value === IS_NOT_SET_FILTER_ITEM.value) {
				onChange(
					{ ...value, isNotSetApplied: true, operator: FilterOperator.Is },
					close
				);
			} else if (item.value === IS_SET_FILTER_ITEM.value) {
				onChange(
					{ ...value, isSetApplied: true, operator: FilterOperator.Is },
					close
				);
			} else if (filterType === FilterType.Single) {
				onChange(
					{
						...value,
						value: item.value,
					},
					close
				);
			} else if (filterType === FilterType.Multiple) {
				let currentValue: FilterValueType[] = [];

				if (!isNil(value.value)) {
					currentValue = Array.isArray(value.value)
						? value.value
						: [value.value];
				}

				onChange(
					{
						...value,
						value: [...currentValue, item.value],
					},
					close
				);
			} else {
				throw new Error('Invalid filter type');
			}
		},
		[filterType, onChange, value]
	);

	const onDeselect = useCallback(
		(item: FilterItem) => (close: boolean) => {
			if (item.value === IS_NOT_SET_FILTER_ITEM.value) {
				onChange(
					{ ...value, isNotSetApplied: false, operator: FilterOperator.Is },
					close
				);
			} else if (item.value === IS_SET_FILTER_ITEM.value) {
				onChange(
					{ ...value, isSetApplied: false, operator: FilterOperator.Is },
					close
				);
			} else if (filterType === FilterType.Single) {
				// selecting a already checked radio button shouldn't do anything
				// NOOP
			} else if (filterType === FilterType.Multiple) {
				let currentValue: FilterValueType[] = [];

				if (!isNil(value.value)) {
					currentValue = Array.isArray(value.value)
						? value.value
						: [value.value];
				}

				const newValue = currentValue.filter(
					(currentValueItem) => currentValueItem !== item.value
				);

				onChange(
					{
						...value,
						value: newValue,
					},
					close
				);
			} else {
				throw new Error('Invalid filter type');
			}
		},
		[filterType, onChange, value]
	);

	const items = useMemo(() => {
		const result: Array<FilterItem | typeof FILTER_OPTIONS_DIVIDER> = [
			...selected,
		];

		if (selected.length > 0 && unselected.length) {
			result.push(FILTER_OPTIONS_DIVIDER);
		}

		result.push(...unselected);

		return result;
	}, [selected, unselected]);

	const handleOnClick = useCallback(
		(isSelected: boolean, item: FilterItem, close?: boolean) => {
			// If `close` is explicitly provided by the child component (i.e. FilterMenuItem)
			// use it; otherwise fall back to our default rule of closing only for single-select
			// dropdowns.
			const shouldClose =
				typeof close === 'boolean' ? close : filterType === FilterType.Single;

			if (isSelected) {
				if (handleOnDeselect) {
					handleOnDeselect(item);
				} else {
					onDeselect(item)(shouldClose);
				}
			} else {
				if (handleOnSelect) {
					handleOnSelect(item);
				} else {
					onSelect(item)(shouldClose);
				}
			}
		},
		[handleOnSelect, handleOnDeselect, onDeselect, onSelect, filterType]
	);

	return (
		<ListBox.ItemsDropdown
			search={{
				onChange: setSearchTerm,
				value: searchTerm,
				placeholder: searchPlaceholder,
			}}
			items={items}
			renderItem={(item, getProps, idx) => {
				if (item === FILTER_OPTIONS_DIVIDER) {
					return <ListBox.Divider key={`divider-${idx}`} />;
				}

				const isSelected = selected.includes(item);

				return (
					<FilterMenuItem
						key={`filter-value-${item.value}`}
						filterType={filterType}
						item={item}
						renderMenuItem={renderMenuItem}
						onClick={(close) => handleOnClick(isSelected, item, close)}
						selected={isSelected ? true : undefined}
						getProps={getProps}
					/>
				);
			}}
		>
			{loading && (
				<Stack spacing="2xs">
					{range(3).map((idx) => (
						<FilterItemSkeleton key={idx} />
					))}
				</Stack>
			)}
			{error && <FilterItemError>An error has ocurred</FilterItemError>}
		</ListBox.ItemsDropdown>
	);
}
